import {
  ArrowLeft,
  ArrowRight,
  History,
  Pin,
  PinOff,
  Trash2,
  EyeOff,
  Maximize2,
  Menu,
  MessageSquare,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  BookOpen,
  ShieldCheck,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { GlobalVoiceRecorder } from '@/components/common/GlobalVoiceRecorder';
import { FeedbackHeaderButton } from '@/components/feedback/FeedbackHeaderButton';
import { TAB_LABELS } from '@/components/superclinic/client-details/constants';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FloatingFocusButton } from '@/components/ui/floating-focus-button';
import { formatPageTitle, getPageTitle } from '@/config/page-titles';
import { useFocusMode } from '@/contexts/FocusModeContext';
import { usePageClientContext } from '@/hooks/usePageClientContext';
import { usePageHistory, type PageVisit } from '@/hooks/usePageHistory';
import { usePermissions } from '@/hooks/usePermissions';
import {
  corporateAdminProfileReturnPath,
  isCorporateEmployeeProfilePath,
} from '@/pages/corporates/corporateEnrollmentDisplay';
import { togglePiiMode, togglePrivacyMode } from '@/redux/privacySlice';
import { AppDispatch, RootState } from '@/redux/store';
import { USER_ROLES } from '@/types/user';
import { hasAnyUserRole } from '@/utils/roles';

interface HeaderProps {
  contentLeft?: number;
  isMobile: boolean;
  sidebarOpen: boolean;
  onSidebarToggle: () => void;
  showSupportActions: boolean;
}

const Header = ({
  contentLeft = 0,
  isMobile,
  sidebarOpen,
  onSidebarToggle,
  showSupportActions,
}: HeaderProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const { getScope, hasPermission } = usePermissions();
  const accountId = useSelector((state: RootState) => state.user.user_id);
  const pageClient = usePageClientContext();
  const clientId = pathname.match(/^\/client\/(?:discovery\/)?([^/]+)/)?.[1];
  const clientName =
    pageClient.pathname === pathname && pageClient.clientId === clientId
      ? pageClient.clientName
      : undefined;
  const userRole = useSelector((state: RootState) => state.user.role);
  const piiMode = useSelector((state: RootState) => state.privacy.piiMode);
  const privacyMode = useSelector((state: RootState) => state.privacy.privacyMode);
  const { isFocusMode, toggleFocusMode } = useFocusMode();
  const isStaff =
    !!userRole && !hasAnyUserRole(userRole, USER_ROLES.CLIENT, USER_ROLES.CORPORATE_ADMIN);
  const showPrivacyToggles = getScope('appointments', 'read') === 'all';
  const canViewPeople = hasPermission('user_management', 'list');
  const title = getPageTitle(pathname);
  const tab = new URLSearchParams(search).get('tab');
  const tabLabel = tab
    ? (TAB_LABELS[tab] ??
      (
        {
          client_data: 'Visit Data',
          precheck: 'Precheck',
          consultation: 'Doctor’s Consultation',
        } as Record<string, string>
      )[tab] ??
      tab.replace(/[_-]/g, ' '))
    : undefined;
  const detailTitle = formatPageTitle(title, tabLabel);
  const {
    currentPage,
    recentPages,
    pinnedPages,
    persistenceFailed,
    canGoBack,
    canGoForward,
    goBack,
    goForward,
    openPage,
    togglePin,
    clearHistory,
  } = usePageHistory(detailTitle, { accountId, clientId, clientName });
  const pageName = (page: PageVisit) =>
    page.clientId ? (piiMode ? 'Client' : page.clientName || 'Client details') : page.title;
  const currentTitle =
    clientId && (clientName || piiMode)
      ? formatPageTitle(pageName(currentPage), detailTitle)
      : detailTitle;
  const renderPage = (page: PageVisit, pinned: boolean) => (
    <div key={page.url} className="flex items-stretch gap-1">
      <DropdownMenuItem
        onSelect={() => openPage(page)}
        className="min-w-0 flex-1 flex-col items-start gap-0.5"
        aria-label={`Open ${pageName(page)} ${page.clientId ? page.title : ''}`}
      >
        <span className="w-full truncate font-medium">{pageName(page)}</span>
        {page.clientId && (
          <span className="w-full truncate text-xs text-muted-foreground">{page.title}</span>
        )}
        {page.url === currentPage.url && <span className="text-xs text-primary">Current page</span>}
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={(event) => {
          event.preventDefault();
          togglePin(page);
        }}
        className="shrink-0 justify-center rounded-md px-3"
        aria-label={`${pinned ? 'Unpin' : 'Pin'} ${pageName(page)} ${page.clientId ? page.title : ''}`}
        title={pinned ? 'Unpin page' : 'Pin page'}
      >
        {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
      </DropdownMenuItem>
    </div>
  );
  const privacyItems = (
    <>
      <DropdownMenuLabel>Privacy</DropdownMenuLabel>
      <DropdownMenuCheckboxItem checked={piiMode} onCheckedChange={() => dispatch(togglePiiMode())}>
        Mask personal details
      </DropdownMenuCheckboxItem>
      <DropdownMenuCheckboxItem
        checked={privacyMode}
        onCheckedChange={() => dispatch(togglePrivacyMode())}
      >
        Hide clinical notes
      </DropdownMenuCheckboxItem>
    </>
  );

  return (
    <header
      aria-label="Workspace toolbar"
      data-testid="header_root"
      style={{ left: contentLeft, height: 'var(--app-toolbar-height)' }}
      className="fixed right-0 top-0 z-40 flex items-center justify-between gap-2 border-b border-border bg-background px-2 pt-[env(safe-area-inset-top)] lg:px-3 lg:pt-0"
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-8 shrink-0 rounded-md lg:h-8 lg:min-h-0 lg:min-w-0"
              aria-label="Navigation and recent pages"
              title="Navigation and recent pages"
            >
              <Menu className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-80 max-w-[calc(100vw-16px)]">
            {!hasAnyUserRole(userRole, USER_ROLES.CORPORATE_ADMIN) &&
              (!isFocusMode || isMobile) && (
                <>
                  <DropdownMenuItem
                    onSelect={onSidebarToggle}
                    data-testid="appContent_sidebarToggle"
                  >
                    {sidebarOpen ? (
                      <PanelLeftClose className="mr-2 h-4 w-4" />
                    ) : (
                      <PanelLeftOpen className="mr-2 h-4 w-4" />
                    )}
                    {isMobile
                      ? sidebarOpen
                        ? 'Close navigation'
                        : 'Open navigation'
                      : sidebarOpen
                        ? 'Hide sidebar'
                        : 'Show sidebar'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
            {pathname.startsWith('/client/') && (
              <DropdownMenuItem asChild>
                <Link to={canViewPeople ? '/users' : '/hub'}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {canViewPeople ? 'Back to people' : 'Back to hub'}
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuLabel>Current page</DropdownMenuLabel>
            {renderPage(
              currentPage,
              pinnedPages.some((page) => page.url === currentPage.url)
            )}
            <DropdownMenuSeparator />
            <div className="max-h-[min(440px,50dvh)] overflow-y-auto">
              {pinnedPages.length > 0 && (
                <>
                  <DropdownMenuLabel className="flex items-center gap-2">
                    <Pin className="h-4 w-4" /> Pinned pages
                  </DropdownMenuLabel>
                  {pinnedPages.map((page) => renderPage(page, true))}
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuLabel className="flex items-center gap-2">
                <History className="h-4 w-4" /> Recently visited
              </DropdownMenuLabel>
              {recentPages.length ? (
                recentPages.map((page) => renderPage(page, false))
              ) : (
                <p className="px-2 py-3 text-sm text-muted-foreground">
                  No recent pages. Pages you visit will appear here.
                </p>
              )}
            </div>
            <DropdownMenuSeparator />
            <p className="px-2 py-1 text-xs leading-relaxed text-muted-foreground">
              Keeps the latest 25 pages. Older entries are removed automatically. Pinned pages stay
              until you unpin them.
            </p>
            {persistenceFailed && (
              <p role="status" className="px-2 py-1 text-xs text-destructive">
                Browser storage is unavailable. Changes will only last until you reload.
              </p>
            )}
            <DropdownMenuItem
              aria-label="Clear history"
              onSelect={(event) => {
                event.preventDefault();
                clearHistory();
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Clear history
              <span className="ml-auto text-xs text-muted-foreground">Keeps pins</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex shrink-0 items-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-8 rounded-md lg:h-8 lg:min-h-0 lg:min-w-0"
            aria-label="Go back"
            title="Go back"
            disabled={
              !canGoBack &&
              !(
                hasAnyUserRole(userRole, USER_ROLES.CORPORATE_ADMIN) &&
                isCorporateEmployeeProfilePath(pathname)
              )
            }
            onClick={() => {
              if (
                hasAnyUserRole(userRole, USER_ROLES.CORPORATE_ADMIN) &&
                isCorporateEmployeeProfilePath(pathname)
              ) {
                navigate(corporateAdminProfileReturnPath(location.state));
                return;
              }
              goBack();
            }}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-8 rounded-md lg:h-8 lg:min-h-0 lg:min-w-0"
            aria-label="Go forward"
            title="Go forward"
            disabled={!canGoForward}
            onClick={goForward}
          >
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
        <span className="truncate text-sm font-medium" title={currentTitle}>
          {currentTitle}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-0.5 lg:gap-1">
        {!hasAnyUserRole(userRole, USER_ROLES.CLIENT) && (
          <Button
            variant="ghost"
            size="sm"
            aria-label="Global search"
            aria-keyshortcuts="Meta+J Control+J Meta+P Control+P"
            onClick={() => window.dispatchEvent(new CustomEvent('openUniversalSearch'))}
            className="h-11 w-9 px-0 lg:h-8 lg:min-h-0 lg:w-auto lg:gap-2 lg:px-2"
            data-testid="header_searchBtn"
          >
            <Search className="h-4 w-4" />
            <span className="hidden lg:inline">Search</span>
            <kbd className="hidden text-[10px] text-muted-foreground 2xl:inline">⌘J · ⌘P</kbd>
          </Button>
        )}
        {isStaff && (
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="h-11 w-9 px-0 lg:h-8 lg:min-h-0 lg:w-auto lg:gap-2 lg:px-2"
          >
            <Link
              to="/knowledge-base"
              aria-label="Knowledge Base"
              aria-current={pathname.startsWith('/knowledge-base') ? 'page' : undefined}
              title="Knowledge Base"
              data-testid="header_knowledgeBaseBtn"
            >
              <BookOpen className="h-4 w-4" />
              <span className="hidden lg:inline">Knowledge Base</span>
            </Link>
          </Button>
        )}
        <GlobalVoiceRecorder
          showVoiceRecorder={isStaff && showSupportActions && !pathname.startsWith('/member')}
          showChatButton={false}
          renderTrigger={(voice) => (
            <>
              <div className="hidden items-center gap-1 lg:flex">
                {voice.visible && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={voice.onClick}
                    disabled={voice.disabled}
                    aria-label={voice.label}
                    aria-pressed={voice.active}
                    className="h-8 min-h-0 gap-2 px-2"
                  >
                    {voice.icon}
                    <span className="hidden min-[1100px]:inline">
                      {voice.status || (voice.active ? 'Recording' : 'Voice note')}
                    </span>
                  </Button>
                )}
                {isStaff && showSupportActions && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/chat')}
                    aria-label="Open chat"
                    className="h-8 min-h-0 gap-2 px-2"
                  >
                    <MessageSquare className="h-4 w-4" />
                    <span className="hidden min-[1100px]:inline">Chat</span>
                  </Button>
                )}
                {isStaff && <FloatingFocusButton inline />}
                {isStaff && showPrivacyToggles && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 min-h-0 gap-2 px-2"
                        aria-label="Privacy controls"
                      >
                        {piiMode || privacyMode ? (
                          <ShieldCheck className="h-4 w-4 text-primary" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                        <span className="hidden min-[1100px]:inline">Privacy</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">{privacyItems}</DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              {isStaff && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="More tools"
                      className="h-11 w-9 rounded-md lg:hidden"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Page tools</DropdownMenuLabel>
                    {voice.visible && (
                      <DropdownMenuItem onSelect={voice.onClick} disabled={voice.disabled}>
                        {voice.icon}
                        <span className="ml-2">{voice.label}</span>
                      </DropdownMenuItem>
                    )}
                    {showSupportActions && (
                      <DropdownMenuItem onSelect={() => navigate('/chat')}>
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Open chat
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onSelect={toggleFocusMode}>
                      <Maximize2 className="mr-2 h-4 w-4" />
                      {isFocusMode ? 'Exit focus mode' : 'Enter focus mode'}
                    </DropdownMenuItem>
                    {showPrivacyToggles && (
                      <>
                        <DropdownMenuSeparator />
                        {privacyItems}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
        />
        <FeedbackHeaderButton />
      </div>
    </header>
  );
};
export default Header;
